import {
  Box,
  Button,
  CascaderField,
  Col,
  ErrorMessage,
  FieldLabel,
  Flex,
  Form,
  Row,
  theme,
} from '@/components';
import { useProjectFormContext } from '@/features/project/_hooks/form-context';
import type { sheetTypeEnum } from '@/utils/enum';
import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { type FC, useCallback } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useSelectFiledFormData } from '../../_hooks/project-form-handler';
import { OrganizationTemplateSelectButton } from './OrganizationTemplateSelectButton';

type Props = {
  type: z.infer<typeof sheetTypeEnum>;
  required?: boolean;
  reviewType?: 'basic' | 'skipped-review';
};

export const RelatedOrganizationArrayField: FC<Props> = ({
  type,
  required,
  reviewType = 'basic',
}) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    watch,
    getValues,
    setValue,
  } = useProjectFormContext();
  const { token } = theme.useToken();

  const [fiscalYear, fiscalYearOfSnapshot] = watch([
    'data.fiscalYear',
    'data.fiscalYearOfSnapshot',
  ]);

  const snapshotId = fiscalYearOfSnapshot?.at(-1);

  const { organizationOptions } = useSelectFiledFormData({
    inputFiscalYear: fiscalYear,
    inputSnapshotId: snapshotId,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'data.nestedRelatedOrganizationIds',
  });

  /**
   * テンプレートから組織を追加する
   * 重複を除いて後ろに追加
   */
  const handleSelectTemplate = useCallback(
    (organizationPaths: string[][]) => {
      const currentValue = getValues('data.nestedRelatedOrganizationIds') ?? [];
      const newPaths: string[][] = [...currentValue];

      // パスを文字列化してSetで管理することで重複チェックを高速化（O(n×m) → O(n)）
      const existingPathStrings = new Set(
        currentValue.map((path) => path.join(',')),
      );

      for (const newPath of organizationPaths) {
        const pathString = newPath.join(',');
        if (!existingPathStrings.has(pathString)) {
          newPaths.push(newPath);
          existingPathStrings.add(pathString);
        }
      }

      setValue('data.nestedRelatedOrganizationIds', newPaths, {
        shouldDirty: true,
      });
    },
    [getValues, setValue],
  );

  return (
    <Form.Item
      label={
        <Flex gap={8} align="center">
          <div>
            <FieldLabel
              style={{ whiteSpace: 'nowrap' }}
              required={required}
              text={t('related-organizations')}
              hint={
                type === 'SS'
                  ? t('hint-related-organization-ids-for-ss')
                  : type === 'KS'
                    ? t('hint-related-organization-ids-for-ks')
                    : reviewType === 'skipped-review'
                      ? t('hint-related-organization-ids-for-review-skipped')
                      : t('hint-related-organization-ids-for-rs')
              }
            />
          </div>
          <OrganizationTemplateSelectButton
            onSelectTemplate={handleSelectTemplate}
            organizationOptions={organizationOptions.data}
            disabled={organizationOptions.isPending}
          />
        </Flex>
      }
    >
      {errors.data?.nestedOtherResponsibleOrganizationIds?.root && (
        <Box marginBottom={12}>
          <ErrorMessage
            errors={errors}
            name="data.nestedRelatedOrganizationIds.root"
          />
        </Box>
      )}

      <Row gutter={[24, 8]}>
        {fields.map((item, idx) => {
          return (
            <Col key={item.id} span={12}>
              <Flex gap={8} align="baseline">
                <Box flex={1}>
                  <CascaderField
                    name={`data.nestedRelatedOrganizationIds.${idx}`}
                    control={control}
                    errors={errors}
                    options={organizationOptions.data}
                    enableIntermediate
                    allowClear={false}
                    wrapperStyle={{ marginBottom: 0 }}
                    loading={organizationOptions.isPending}
                    textNotInOption={t('not-exist-in-organization-options')}
                  />
                </Box>
                <CloseCircleOutlined
                  style={{ color: token.colorPrimary, padding: 8, margin: -8 }}
                  onClick={() => {
                    remove(idx);
                  }}
                />
              </Flex>
            </Col>
          );
        })}
        <Col span={24}>
          <Row gutter={[8, 0]}>
            <Col span={12}>
              <Button
                type="dashed"
                onClick={() => {
                  append([[]]);
                }}
                block
                icon={<PlusOutlined />}
              >
                {t('add-related-organization')}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      {errors.data?.nestedRelatedOrganizationIds?.message && (
        <ErrorMessage
          errors={errors}
          name="data.nestedRelatedOrganizationIds"
        />
      )}
    </Form.Item>
  );
};
